import { create } from 'zustand'
import { Device, Call } from '@twilio/voice-sdk'
import { apiClient } from '@/lib/api'
import { useUIStore } from '@/stores/uiStore'
import { normalizeUsPhone } from '@/utils/phone'
import { logger } from '../lib/logger'
import { captureException } from '@/lib/errorTracking'
import {
  isThisTabLeader,
  onLeaderChange,
  startLeaderElection,
} from '@/lib/crossTabLeader'

export interface CallerInfo {
  number: string
  name?: string
  contactId?: string
}

export type CallStatus = 'idle' | 'ringing' | 'connecting' | 'connected' | 'ended'
export type TransferStatus = 'idle' | 'connecting' | 'connected' | 'completed' | 'cancelled'
export type ConnectionQuality = 'good' | 'fair' | 'poor'
export type SoftphoneTab = 'keypad' | 'contacts' | 'history'

/**
 * Shape passed by softphone tab components (DialPad, ContactPickerTab) to
 * their `onCall` prop. `Softphone.tsx` adapts this into an `InitiateCallArgs`
 * call on the store — tab components never talk to `initiateCall` directly.
 */
export interface MakeCallMeta {
  name?: string
  contactId?: string
}

// ─── Post-call card (AC-38) ──────────────────────────────────────────────────
export interface LastEndedCall {
  callSid?: string
  contactId?: string
  contactName?: string
  number: string
  duration: number
  direction: 'inbound' | 'outbound'
  context?: OutboundCallContext
  endedAt: number
  finalQuality?: ConnectionQuality
}

// ─── Outbound call context (AC-11) ───────────────────────────────────────────
// Originating-record IDs attached to an outbound call. These are forwarded to
// Twilio via device.connect({ params: { ... } }) so the backend can persist
// them onto the CallSession row when the call lands at /api/telephony/incoming.
export interface OutboundCallContext {
  jobId?: string
  quoteId?: string
  estimateId?: string
}

export interface InitiateCallArgs {
  contactId?: string
  phone: string
  context?: OutboundCallContext
  override?: { reason: string }
  /**
   * Optional display name for the active-call card. Shown synchronously
   * before the backend's screen-pop enrichment lands, so the UI doesn't
   * flash the raw E.164 when the caller is a known contact.
   */
  name?: string
}

// ─── Blocked-call reasons (AC-14) ─────────────────────────────────────────────
// When /api/telephony/prepare-call returns 412, the store stashes this so the
// app-root modal can render the quiet-hours blocking UI. (TCPA consent gate
// was removed on 2026-04-21 per product decision.)
export type BlockedCallReason = 'quiet_hours'

export interface BlockedCall {
  contactId?: string
  phone: string
  context?: OutboundCallContext
  reason: BlockedCallReason
  details?: {
    localHour?: number
    timezone?: string
    [key: string]: unknown
  }
}

interface CallState {
  device: Device | null
  activeCall: Call | null
  callStatus: CallStatus
  callDirection: 'inbound' | 'outbound' | null
  /**
   * Twilio CallSid captured from the accept signaling message. Used by
   * holdCall/resumeCall/initiateWarmTransfer so they don't rely on
   * `activeCall.parameters.CallSid` which can be racy for outbound calls
   * (populated only after Twilio's answer message — if hold is clicked
   * before that round-trips, parameters.CallSid is still undefined).
   */
  activeCallSid: string | null
  isMuted: boolean
  isOnHold: boolean
  /**
   * Speakerphone state (mobile-only UX). On desktop the browser already
   * routes to the selected system output; on mobile browsers the default
   * sink is the receiver/earpiece and users need an explicit way to flip
   * to the loudspeaker. `null` = feature not available (iOS Safari doesn't
   * expose audiooutput devices — the toggle is hidden there).
   */
  isSpeakerOn: boolean | null
  callerInfo: CallerInfo | null
  incomingCall: Call | null
  callStartTime: number | null
  dialPadOpen: boolean
  softphoneExpanded: boolean
  softphoneTab: SoftphoneTab
  error: string | null

  // Blocked-call state (quiet hours pre-check)
  blockedCall: BlockedCall | null

  // Transfer state
  transferStatus: TransferStatus
  transferTarget: string | null
  conferenceSid: string | null
  transferCallSid: string | null
  originalCallSid: string | null

  // ─── AC-34 Connection quality ────────────────────────────────────────────
  connectionQuality: ConnectionQuality | null

  // ─── AC-4 Token refresh resilience ──────────────────────────────────────
  // phoneOffline flips true when token refresh fails (tokenWillExpire handler
  // or manual retry) and clears on the first successful refresh. The NotificationBanner
  // renders "Phone offline — reconnecting…" while this is true.
  phoneOffline: boolean
  // Flips true after MAX_TOKEN_REFRESH_ATTEMPTS consecutive failures; retrying
  // stops and the banner shows "Phone unavailable — contact support".
  tokenRefreshExhausted: boolean

  // ─── Mic permission ──────────────────────────────────────────────────────
  // Flips true when Twilio Device throws NotAllowedError / Permission denied,
  // OR getUserMedia({audio:true}) rejects. The MicPermissionBanner watches
  // this to render the "Enable microphone" UI. Cleared by retryMicPermission().
  micPermissionDenied: boolean

  // ─── AC-38 Post-call card ────────────────────────────────────────────────
  lastEndedCall: LastEndedCall | null

  // ─── AC-11 outbound context retained through the call for post-call card ─
  pendingContext: OutboundCallContext | null
  pendingContactId: string | null

  // ─── Remote audio element (iOS one-way-audio fix) ────────────────────────
  // On iOS Safari, HTMLAudioElement.setSinkId is undefined → Twilio's SDK
  // falls into `_fallbackOnAddTrack`, which creates a detached <audio>
  // element and calls .play() from inside the WebRTC ontrack callback —
  // NOT a user gesture context. iOS' autoplay policy rejects that play()
  // and the user hears nothing while still being heard fine on the remote
  // end. We sidestep the SDK's detached element by mounting our own
  // <audio autoPlay playsInline> element, registering it here, and
  // attaching call.getRemoteStream() to it on 'accept' — within the
  // user-gesture activation stack from the Accept/Place-call click.
  remoteAudioElement: HTMLAudioElement | null

  // Actions
  initDevice: () => Promise<void>
  destroyDevice: () => void
  acceptCall: () => void
  rejectCall: () => void
  hangUp: () => void
  toggleMute: () => void
  holdCall: () => Promise<void>
  resumeCall: () => Promise<void>
  /**
   * Toggle browser audio output between the default sink (earpiece on
   * mobile) and the loudspeaker. No-op when `isSpeakerOn` is null
   * (feature unavailable in this browser).
   */
  toggleSpeaker: () => Promise<void>
  /** Legacy dial-pad entry point — forwards to initiateCall(). */
  makeCall: (number: string, meta?: MakeCallMeta) => Promise<void>
  /**
   * Outbound call flow (AC-11, AC-14). Runs the quiet-hours pre-check via
   * /api/telephony/prepare-call; on 412 stashes blockedCall so the UI renders
   * the quiet-hours modal. On allowed, dials via device.connect() with the
   * originating record IDs attached.
   */
  initiateCall: (args: InitiateCallArgs) => Promise<void>
  clearBlockedCall: () => void
  setDialPadOpen: (open: boolean) => void
  setSoftphoneExpanded: (expanded: boolean) => void
  setSoftphoneTab: (tab: SoftphoneTab) => void
  clearError: () => void
  retryMicPermission: () => Promise<void>
  /**
   * Manually retry Twilio Device registration after the circuit breaker
   * tripped (tokenRefreshExhausted). Resets the breaker so a fresh sequence
   * can run, then either re-runs the refresh loop on the existing Device or
   * spins up a new one via initDevice() if it was already torn down.
   * Wired to the "Reconnect" button on PhoneOfflineBanner.
   */
  forceReconnect: () => Promise<void>

  // Transfer actions
  initiateWarmTransfer: (targetNumber: string) => Promise<void>
  completeWarmTransfer: () => Promise<void>
  cancelWarmTransfer: () => Promise<void>
  resetTransfer: () => void

  // Post-call card (AC-38)
  clearLastEndedCall: () => void

  // Remote-audio element wiring (iOS one-way-audio fix)
  /**
   * Softphone.tsx calls this on mount with a hidden <audio autoPlay
   * playsInline> element it created. The element MUST be created in
   * the React tree (not via document.createElement inside the SDK) so
   * iOS Safari treats the user's tap on Accept / Place-call as the
   * activation event for THIS element — which is what unlocks .play().
   */
  setRemoteAudioElement: (el: HTMLAudioElement | null) => void
  /**
   * Call inside the synchronous user-gesture stack from a click handler
   * (acceptCall / initiateCall). Issues a no-op play() on the registered
   * audio element so iOS marks it as user-activated; subsequent srcObject
   * assignments + play() from non-gesture contexts (e.g. the 'accept'
   * event handler) then succeed. Safe to call when there's no stream yet.
   */
  primeRemoteAudio: () => void
}

export const useCallStore = create<CallState>((set, get) => ({
  device: null,
  activeCall: null,
  activeCallSid: null,
  callStatus: 'idle',
  callDirection: null,
  isMuted: false,
  isOnHold: false,
  // Probed lazily in initDevice — stays `null` when the browser has no
  // audiooutput devices (iOS Safari). The UI reads null as "hide the toggle".
  isSpeakerOn: null,
  callerInfo: null,
  incomingCall: null,
  callStartTime: null,
  dialPadOpen: false,
  softphoneExpanded: false,
  softphoneTab: 'keypad',
  error: null,
  micPermissionDenied: false,

  blockedCall: null,

  // Transfer defaults
  transferStatus: 'idle',
  transferTarget: null,
  conferenceSid: null,
  transferCallSid: null,
  originalCallSid: null,

  connectionQuality: null,
  // Rehydrate post-call card from sessionStorage so a reload mid- or
  // just-after a call doesn't drop the card on the floor (Theme 6).
  lastEndedCall: loadPersistedLastEndedCall(),
  pendingContext: null,
  pendingContactId: null,
  phoneOffline: false,
  tokenRefreshExhausted: false,
  remoteAudioElement: null,

  setRemoteAudioElement: (el) => set({ remoteAudioElement: el }),

  primeRemoteAudio: () => {
    const el = get().remoteAudioElement
    if (!el) return
    // Issuing .play() inside the click handler unlocks the element for
    // iOS Safari. The play() promise will reject when srcObject is null
    // ("the play() request was interrupted") — that's fine; the element
    // is still marked user-activated and subsequent play() calls work.
    try {
      const p = el.play()
      if (p && typeof p.catch === 'function') p.catch(() => undefined)
    } catch {
      /* iOS rejects with TypeError if no source — harmless, we still get the unlock */
    }
  },

  initDevice: async () => {
    // Guard against re-initialization if a device is already active.
    // This prevents duplicate Device instances from competing to receive calls
    // when React Strict Mode double-invokes effects, or on hot reload.
    const existing = get().device
    if (existing) return
    // Cross-tab leader election: only the elected leader holds an active
    // twilio.Device — otherwise multiple tabs ring simultaneously and the
    // user gets duplicate answer buttons. Start election (idempotent) and
    // wire teardown on leadership loss / re-init on leadership gain.
    ensureLeaderWired()
    if (!isThisTabLeader()) return
    // StrictMode fires two synchronous invocations before either `await`
    // resolves, so `get().device` is `null` on both. Use a module-scoped
    // in-flight flag to serialize the two calls.
    if (initInFlight) return
    initInFlight = true
    const myToken = ++initDeviceToken

    try {
      const { data } = await apiClient.get('/api/telephony/token')
      if (myToken !== initDeviceToken) return
      const token = data?.data?.token
      if (!token) {
        // No token available (backend offline or Twilio not configured)
        // Silently skip initialization for dev mode
        return
      }

      const device = new Device(token, {
        edge: 'ashburn',
        // INFO — we need Twilio's diagnostic chatter in prod to triage
        // one-way-audio / failed-media reports.
        logLevel: 3,
      })
      // device.audio is null until _setupAudioHelper() runs during register(),
      // so setAudioConstraints() and speakerDevices.set() must happen AFTER
      // register resolves — not synchronously after `new Device()`. Prior to
      // this fix, pinning audio constraints + speaker routing was a silent
      // no-op which left remote audio playback unwired on some browsers.

      device.on('incoming', (call: Call) => {
        const params = call.parameters || {}
        const from = params.From || 'Unknown'
        // Theme 6: drop any previous post-call card so it can't bleed into
        // this new call's UI.
        clearPersistedLastEndedCall()
        set({
          incomingCall: call,
          callStatus: 'ringing',
          callDirection: 'inbound',
          callerInfo: {
            number: from,
          },
          softphoneExpanded: true,
          lastEndedCall: null,
        })

        // Non-blocking contact lookup: enrich callerInfo with name + contactId.
        // Failure must never block or delay call answering.
        void (async () => {
          try {
            const res = await apiClient.get<{ data: { items: Array<{ id: string; firstName: string; lastName: string; phone: string | null }> } }>(
              '/api/contacts',
              { params: { search: from, take: 5 } },
            )
            const items = res.data?.data?.items ?? []
            const normalizedFrom = normalizeUsPhone(from) ?? from
            const match = items.find((c) => {
              const normalized = c.phone ? (normalizeUsPhone(c.phone) ?? c.phone) : null
              return normalized === normalizedFrom
            })
            if (match) {
              const name = [match.firstName, match.lastName].filter(Boolean).join(' ') || undefined
              set((s) => {
                if (s.callerInfo?.number !== from) return {}
                return { callerInfo: { number: from, name, contactId: match.id } }
              })
            }
          } catch {
            // Silent — never disrupt call flow
          }
        })()

        // AC-20: OS-level desktop notification on every inbound ring.
        // Fires regardless of tab focus. Click focuses the softphone tab.
        try {
          if (
            typeof window !== 'undefined' &&
            'Notification' in window &&
            Notification.permission === 'granted'
          ) {
            const notif = new Notification(`Incoming call from ${from}`, {
              body: from,
              tag: 'incoming-call',
              icon: '/logo.svg',
            })
            notif.onclick = () => {
              window.focus()
              window.dispatchEvent(new CustomEvent('softphone:focus'))
              notif.close()
            }
          }
        } catch {
          /* Notifications not available — silent no-op. */
        }

        // AC-21: autoplay-safe ringtone. If the user has not interacted yet,
        // skip audio and start a flashing-favicon fallback. When they've
        // interacted, device.on('incoming') handles the audio internally via
        // Twilio's default incoming-ring element.
        const { hasUserInteracted } = useUIStore.getState()
        if (!hasUserInteracted) {
          startFaviconFlash()
        }
      })

      device.on('error', (error: { message?: string; name?: string; code?: number }) => {
        logger.error('[Softphone] Device error:', error)
        const msg = error.message || ''
        const name = error.name || ''
        const permissionDenied =
          name === 'NotAllowedError' ||
          /permission denied/i.test(msg) ||
          /microphone/i.test(msg) && /denied|blocked/i.test(msg)
        if (permissionDenied) {
          set({
            micPermissionDenied: true,
            error: 'Microphone blocked. Enable mic access in your browser, then retry.',
          })
          return
        }
        // Token errors (expired, invalid identity, invalid grants, invalid client name)
        // are recoverable — silently attempt refresh rather than alarming the user.
        // Twilio SDK error codes: 31204 (invalid identity), 31205 (expired),
        // 31206 (invalid grants), 31208 (invalid client name).
        // If all refresh retries fail, attemptTokenRefresh sets phoneOffline:true
        // and the PhoneOfflineBanner is shown instead of a raw error toast.
        const TOKEN_ERROR_CODES = [31204, 31205, 31206, 31208]
        // FOR-TRANSPORT — WebSocket signaling is gone:
        //   31005  ConnectionError — "Server is unavailable" / WS handshake failed
        //   31009  TransportError — "No transport available to send or receive messages"
        // These fire on network change, app background→foreground, or token-expired
        // mid-WS. Same recovery path as token errors: silent fresh token + register.
        const TRANSPORT_ERROR_CODES = [31005, 31009]
        const isTokenError =
          (typeof error.code === 'number' && TOKEN_ERROR_CODES.includes(error.code)) ||
          /invalid.token/i.test(msg) ||
          /token.*(expired|invalid)/i.test(msg)
        const isTransportError =
          (typeof error.code === 'number' && TRANSPORT_ERROR_CODES.includes(error.code)) ||
          /no transport available/i.test(msg)
        if (isTokenError || isTransportError) {
          logger.warn('[Softphone] Recoverable Device error — silent refresh', {
            code: error.code,
            msg,
            kind: isTokenError ? 'token' : 'transport',
          })
          // Surface to Sentry so we can spot a sudden spike of 31009s in prod.
          captureException(error, {
            flow: 'twilio-transport',
            code: error.code,
            kind: isTokenError ? 'token' : 'transport',
          })
          set({ phoneOffline: true })
          attemptTokenRefresh(device, 0)
          return
        }
        captureException(error, { flow: 'twilio-device', code: error.code })
        set({ error: error.message || 'Device error' })
      })

      // AC-4: token refresh with exponential backoff (1s, 2s, 4s, 8s, 16s, capped at 30s)
      // and a visible "Phone offline — reconnecting…" banner while retrying.
      // Both clear on the first successful refresh.
      device.on('tokenWillExpire', () => {
        attemptTokenRefresh(device, 0)
      })

      // FOR-TRANSPORT — Twilio Voice SDK v2 emits 'registered' / 'unregistered'
      // as the analog to v1's 'ready' / 'offline'. unregistered fires when the
      // signaling transport drops (network change, app backgrounded on iOS,
      // token expired mid-WS) — without re-registering, every inbound call
      // hits the user's voicemail and outbound dialing surfaces the 31009 toast.
      device.on('unregistered', () => {
        // Bail if the device in the store no longer matches this one — could
        // be a normal destroyDevice() teardown, in which case we don't want
        // to spin up a reconnect for a dead Device.
        if (useCallStore.getState().device !== device) return
        logger.warn('[Softphone] Device unregistered — attempting silent re-register')
        set({ phoneOffline: true })
        attemptTokenRefresh(device, 0)
      })

      device.on('registered', () => {
        // Banner clears + breaker resets the moment the Device is back online.
        useCallStore.setState({ phoneOffline: false, tokenRefreshExhausted: false })
      })

      await device.register()
      if (myToken !== initDeviceToken) return

      // Wire network-change recovery once per process. The browser's 'online'
      // event and document visibility transitions are the two signals we get
      // when a backgrounded tab / mobile lock-screen / wifi-switch puts the
      // signaling WS to sleep. Twilio's SDK doesn't reconnect on those by
      // itself in every browser — we have to nudge it.
      ensureNetworkListenersWired()

      // Wire audio now that device.audio has been initialized by _setupAudioHelper.
      // (Before register, device.audio is null.) This is the fix for the
      // one-way-audio bug: before, setAudioConstraints was a no-op AND no
      // speaker sink was ever pinned, so on some browsers the remote audio
      // track had nowhere to render → user heard nothing, but their mic
      // still captured fine.
      type AudioHelperLike = {
        setAudioConstraints?: (c: MediaTrackConstraints) => void
        speakerDevices?: { set: (ids: string | string[]) => Promise<void> | void }
        incoming?: (enabled: boolean) => void
      }
      const audio = device.audio as unknown as AudioHelperLike | null
      if (audio) {
        try {
          audio.setAudioConstraints?.({
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          })
        } catch (err) {
          logger.warn('[Softphone] setAudioConstraints failed', err)
        }
        // Pin output sink to the browser default so Twilio's remote-audio
        // <audio> element has a routed destination. Without this, Chrome
        // + some Safari builds render the call but output to nowhere.
        try {
          await Promise.resolve(audio.speakerDevices?.set('default'))
        } catch (err) {
          logger.warn('[Softphone] speakerDevices.set failed', err)
        }
        // Enable incoming-sound playback (ringtones for inbound) — this is
        // on by default in v2 but we set it explicitly so a future SDK
        // default change doesn't silently break audible ring.
        try {
          audio.incoming?.(true)
        } catch {
          /* older SDK — no-op */
        }
        // Audio-route capability: if HTMLAudioElement.setSinkId exists
        // (Android Chrome, desktop Chrome/Edge), expose the speaker toggle
        // and default to loudspeaker so users hear the call without
        // hunting for the button. iOS Safari has no setSinkId — we stay
        // null and the UI hides the toggle entirely.
        if (audioRouteSupported()) {
          set({ isSpeakerOn: true })
        }
      }

      // Resume the shared AudioContext if it booted suspended (Safari/iOS
      // autoplay policy). Without this, Twilio's sound playback (ringtone,
      // DTMF feedback, and — critically on iOS — remote audio mixed via
      // Web Audio) stays silent even after `device.register()` succeeds.
      type DeviceStatic = { audioContext?: AudioContext }
      const ctx = (Device as unknown as DeviceStatic).audioContext
      if (ctx && ctx.state === 'suspended') {
        try {
          await ctx.resume()
        } catch (err) {
          logger.warn('[Softphone] AudioContext resume failed', err)
        }
      }

      set({ device, error: null })
    } catch (err) {
      if (myToken !== initDeviceToken) return
      // Silently handle in dev (Twilio may not be configured), but surface
      // anything noteworthy to the console so prod failures are diagnosable.
      logger.warn('[Softphone] Could not initialize Twilio device', err)
    } finally {
      if (myToken === initDeviceToken) initInFlight = false
    }
  },

  destroyDevice: () => {
    const { device, activeCall } = get()
    if (activeCall) {
      activeCall.disconnect()
    }
    if (device) {
      device.destroy()
    }
    // AC-4: cancel any pending timer AND invalidate in-flight run() closures
    // so they can't call device.updateToken() or reset phoneOffline after the
    // device has already been torn down.
    cancelTokenRefresh()
    initDeviceToken++
    initInFlight = false
    set({
      device: null,
      activeCall: null,
      activeCallSid: null,
      callStatus: 'idle',
      callDirection: null,
      isMuted: false,
      isOnHold: false,
      isSpeakerOn: null,
      callerInfo: null,
      incomingCall: null,
      callStartTime: null,
      phoneOffline: false,
      tokenRefreshExhausted: false,
    })
  },

  acceptCall: () => {
    const { incomingCall } = get()
    if (!incomingCall) return

    // iOS one-way-audio fix: prime our remote-audio element NOW, inside the
    // synchronous user-gesture stack from the Accept tap. This unlocks .play()
    // for the element so the later attach (in the 'accept' event handler,
    // which is no longer in a gesture stack) succeeds on iOS Safari.
    get().primeRemoteAudio()
    incomingCall.accept()
    stopFaviconFlash()

    const setupCallListeners = (call: Call) => {
      // 'accept' fires when the connection is established — start the timer here,
      // not immediately after calling accept(), to avoid counting connection time.
      call.on('accept', () => {
        const sid = (call.parameters as Record<string, string>)?.CallSid || null
        set({ callStatus: 'connected', callStartTime: Date.now(), activeCallSid: sid })
        // Attach the remote audio stream to OUR <audio> element. On iOS Safari
        // this is the only way the user hears the remote party (see fix notes
        // on attachRemoteAudioToCall + iOS setSinkId gap).
        attachRemoteAudioToCall(call, get().remoteAudioElement)
        startQualitySampling(call)
      })
      call.on('disconnect', () => {
        captureLastEndedCall(call, 'inbound')
        stopQualitySampling()
        set({
          activeCall: null,
          activeCallSid: null,
          callStatus: 'ended',
          incomingCall: null,
          isMuted: false,
          isOnHold: false,
          callStartTime: null,
        })
        // Reset to idle after a brief delay
        setTimeout(() => set({ callStatus: 'idle', callerInfo: null, callDirection: null }), 2000)
      })
      call.on('cancel', () => {
        stopFaviconFlash()
        stopQualitySampling()
        set({
          activeCall: null,
          activeCallSid: null,
          callStatus: 'idle',
          incomingCall: null,
          callerInfo: null,
          callDirection: null,
          callStartTime: null,
        })
      })
    }

    setupCallListeners(incomingCall)

    set({
      activeCall: incomingCall,
      incomingCall: null,
      // Use 'connecting' until the 'accept' event fires to show the right state
      callStatus: 'connecting',
    })
  },

  rejectCall: () => {
    const { incomingCall } = get()
    if (!incomingCall) return

    incomingCall.reject()
    stopFaviconFlash()
    set({
      incomingCall: null,
      callStatus: 'idle',
      callerInfo: null,
      callDirection: null,
    })
  },

  hangUp: () => {
    const { activeCall, incomingCall, callDirection, remoteAudioElement } = get()
    if (activeCall) {
      captureLastEndedCall(activeCall, callDirection ?? 'outbound')
      activeCall.disconnect()
    }
    if (incomingCall) {
      incomingCall.reject()
    }
    // Clear our remote-audio element so a stale MediaStream doesn't keep
    // a reference alive past the call. play() races are harmless here.
    if (remoteAudioElement) {
      try {
        remoteAudioElement.pause()
        remoteAudioElement.srcObject = null
      } catch {
        /* element may already be detached — no-op */
      }
    }
    stopFaviconFlash()
    stopQualitySampling()
    set({
      activeCall: null,
      activeCallSid: null,
      incomingCall: null,
      callStatus: 'ended',
      isMuted: false,
      isOnHold: false,
      callStartTime: null,
    })
    setTimeout(() => set({ callStatus: 'idle', callerInfo: null, callDirection: null }), 2000)
  },

  toggleMute: () => {
    const { activeCall, isMuted } = get()
    if (!activeCall) return

    const next = !isMuted
    try {
      activeCall.mute(next)
    } catch (err) {
      logger.warn('[Softphone] mute() threw', err)
    }
    // Re-read Twilio's view of mute state so the UI never disagrees with
    // the SDK — e.g. if mute() no-ops during connecting, isMuted reflects
    // reality, not our optimistic toggle.
    let actual = next
    try {
      actual = activeCall.isMuted()
    } catch {
      /* older SDK — trust our optimistic value */
    }
    set({ isMuted: actual })
  },

  // AC-16: Hold places the remote party on hold via Twilio REST — not a local
  // mute workaround. POST /api/telephony/hold enqueues hold-music on the
  // remote leg; /api/telephony/resume takes it back off hold.
  //
  // Prefers the SID captured from the 'accept' signaling message
  // (`activeCallSid`) over `call.parameters.CallSid` — for outbound calls the
  // parameters bag can still be unpopulated if Twilio's answer message was
  // interpreted differently than expected, whereas activeCallSid is set
  // inside our own accept handler so we know it's there by the time any
  // in-call button is rendered.
  holdCall: async () => {
    const { activeCall, activeCallSid } = get()
    if (!activeCall) return
    const callSid = activeCallSid || (activeCall.parameters as Record<string, string>)?.CallSid
    if (!callSid) {
      set({ error: 'Call not yet established — wait for connect.' })
      return
    }
    try {
      await apiClient.post('/api/telephony/hold', { callSid })
      set({ isOnHold: true })
    } catch (err) {
      logger.warn('[Softphone] hold failed', err)
      set({ error: 'Failed to hold call' })
    }
  },

  resumeCall: async () => {
    const { activeCall, activeCallSid } = get()
    if (!activeCall) return
    const callSid = activeCallSid || (activeCall.parameters as Record<string, string>)?.CallSid
    if (!callSid) {
      set({ error: 'Call not yet established — wait for connect.' })
      return
    }
    try {
      await apiClient.post('/api/telephony/resume', { callSid })
      set({ isOnHold: false })
    } catch (err) {
      logger.warn('[Softphone] resume failed', err)
      set({ error: 'Failed to resume call' })
    }
  },

  toggleSpeaker: async () => {
    // `isSpeakerOn === null` means setSinkId is unavailable (iOS Safari).
    // Don't pretend to flip state — UI hides the button in this case.
    const { isSpeakerOn, remoteAudioElement } = get()
    if (isSpeakerOn === null) return
    if (!remoteAudioElement) {
      // No call yet — track the desired state so the next attach honors it.
      set({ isSpeakerOn: !isSpeakerOn })
      return
    }
    const next = !isSpeakerOn
    const ok = await applyAudioRoute(remoteAudioElement, next ? 'speaker' : 'earpiece')
    if (ok) {
      set({ isSpeakerOn: next })
    } else {
      logger.warn('[Softphone] toggleSpeaker: setSinkId failed, leaving state unchanged')
    }
  },

  /**
   * AC-11 / AC-14: outbound call flow with quiet-hours pre-check.
   *
   *  1. POST /api/telephony/prepare-call { contactId, override? }
   *  2. On 412 blocked, stash { blockedCall } for the app-root modal.
   *  3. On allowed, device.connect with originating-record IDs in params
   *     — the backend persists them to CallSession via /api/telephony/incoming.
   */
  initiateCall: async ({ contactId, phone, context, override, name }: InitiateCallArgs) => {
    const { device } = get()
    if (!device) {
      set({ error: 'Phone not connected. Please check your settings.' })
      return
    }

    // Flo-Rite is US-only — accept 10-digit / 11-digit-leading-1 / +1 inputs
    // and normalize to E.164 before Twilio. Bare 10-digit dials previously
    // round-tripped to /incoming as `To=5551234567`, where isValidE164
    // rejected them and returned `<Hangup/>` — the "ring then hang up"
    // symptom Gaige reported.
    const normalizedPhone = normalizeUsPhone(phone)
    if (!normalizedPhone) {
      set({ error: 'Enter a valid US phone number (10 digits).' })
      return
    }
    phone = normalizedPhone

    // ── Pre-check (quiet hours) ───────────────────────────────────────────
    try {
      const { data } = await apiClient.post('/api/telephony/prepare-call', {
        contactId,
        phone,
        override,
        context,
      })
      const payload = data?.data ?? data
      if (payload?.blocked) {
        set({
          blockedCall: {
            contactId,
            phone,
            context,
            reason: payload.reason as BlockedCallReason,
            details: payload.details,
          },
        })
        return
      }

      // ── Dial with context forwarded as Twilio params (AC-11) ────────────
      const callToken: string | undefined = payload?.callToken
      const connectParams: Record<string, string> = { To: phone }
      if (contactId) connectParams.ContactId = contactId
      if (callToken) connectParams.CallToken = callToken
      if (context?.jobId) connectParams.JobId = context.jobId
      if (context?.quoteId) connectParams.QuoteId = context.quoteId
      if (context?.estimateId) connectParams.EstimateId = context.estimateId

      // iOS one-way-audio fix: prime the remote-audio element inside this
      // user-gesture stack from the click that triggered initiateCall. See
      // attachRemoteAudioToCall comment for the full explanation.
      get().primeRemoteAudio()
      // Populate callerInfo immediately so the active-call card can show the
      // contact's name while we wait for device.connect to resolve. Screen-pop
      // can still overwrite this later with backend-enriched data.
      // Theme 6: clear any previous post-call card so it doesn't bleed into
      // the new outbound call.
      clearPersistedLastEndedCall()
      set({
        callStatus: 'connecting',
        callDirection: 'outbound',
        callerInfo: { number: phone, contactId, name },
        softphoneExpanded: true,
        blockedCall: null,
        pendingContext: context ?? null,
        pendingContactId: contactId ?? null,
        lastEndedCall: null,
      })

      const call = await device.connect({ params: connectParams })

      call.on('accept', () => {
        const sid = (call.parameters as Record<string, string>)?.CallSid || null
        set({ callStatus: 'connected', callStartTime: Date.now(), activeCallSid: sid })
        // iOS one-way-audio fix: attach the remote stream to our <audio>
        // element so the user can hear the other party on iOS Safari.
        attachRemoteAudioToCall(call, get().remoteAudioElement)
        startQualitySampling(call)
      })

      call.on('disconnect', () => {
        captureLastEndedCall(call, 'outbound')
        stopQualitySampling()
        set({
          activeCall: null,
          activeCallSid: null,
          callStatus: 'ended',
          isMuted: false,
          isOnHold: false,
          callStartTime: null,
        })
        setTimeout(() => set({ callStatus: 'idle', callerInfo: null, callDirection: null }), 2000)
      })

      call.on('cancel', () => {
        stopQualitySampling()
        set({
          activeCall: null,
          activeCallSid: null,
          callStatus: 'idle',
          callerInfo: null,
          callDirection: null,
          callStartTime: null,
        })
      })

      call.on('error', (error: { message?: string }) => {
        logger.error('[Softphone] Call error:', error)
        set({
          error: error.message || 'Call failed',
          callStatus: 'idle',
          callerInfo: null,
          callDirection: null,
        })
      })

      set({ activeCall: call })
    } catch (err: unknown) {
      // 412 from prepare-call → blocked call (quiet hours).
      // Axios shape: err.response.status / err.response.data
      const response = (err as { response?: { status?: number; data?: { reason?: BlockedCallReason; details?: Record<string, unknown>; data?: { reason?: BlockedCallReason; details?: Record<string, unknown> } } } }).response
      if (response?.status === 412) {
        const body = response.data?.data ?? response.data
        if (body?.reason) {
          set({
            blockedCall: {
              contactId,
              phone,
              context,
              reason: body.reason as BlockedCallReason,
              details: body.details,
            },
          })
          return
        }
      }
      set({
        error: 'Failed to place call',
        callStatus: 'idle',
        callerInfo: null,
        callDirection: null,
      })
    }
  },

  clearBlockedCall: () => set({ blockedCall: null }),

  /**
   * Legacy entry point used by DialPad / SoftphonePanel / ContactPickerTab —
   * forwards to `initiateCall` with optional name + contactId metadata.
   * Does NOT carry outbound context (jobId/quoteId/estimateId) — callers with
   * a record-level context should call `initiateCall` directly (see ClickToCall).
   */
  makeCall: async (number: string, meta?: MakeCallMeta) => {
    await (get().initiateCall({
      phone: number,
      name: meta?.name,
      contactId: meta?.contactId,
    }))
  },

  setDialPadOpen: (open: boolean) => set({ dialPadOpen: open }),
  setSoftphoneExpanded: (expanded: boolean) => set({ softphoneExpanded: expanded }),
  setSoftphoneTab: (tab: SoftphoneTab) => set({ softphoneTab: tab }),
  clearError: () => set({ error: null }),

  retryMicPermission: async () => {
    // Explicit getUserMedia() re-request. Browsers will (a) re-prompt if the
    // site has no stored decision, or (b) throw NotAllowedError immediately
    // if the user has set the permission to "blocked" at the site level —
    // in which case we leave the banner up with a message pointing them to
    // their browser settings.
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      set({ error: 'This browser does not support microphone access.' })
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      // Release immediately — Twilio will re-acquire on call.
      stream.getTracks().forEach((t) => t.stop())
      set({ micPermissionDenied: false, error: null })
      // Re-init device now that mic is granted.
      await get().initDevice()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Microphone access denied.'
      set({
        micPermissionDenied: true,
        error: `${msg} Enable microphone in your browser's site settings.`,
      })
    }
  },

  // ─── Transfer actions ────────────────────────────────────────────────────

  initiateWarmTransfer: async (targetNumber: string) => {
    const { activeCall, activeCallSid } = get()
    if (!activeCall) {
      set({ error: 'No active call to transfer' })
      return
    }

    const callSid = activeCallSid || (activeCall.parameters as Record<string, string>)?.CallSid || ''
    if (!callSid) {
      set({ error: 'Call not yet established — wait for connect.' })
      return
    }

    set({ transferStatus: 'connecting', transferTarget: targetNumber })

    try {
      const { data } = await apiClient.post('/api/telephony/transfer/warm', {
        callSid,
        targetNumber,
      })
      const result = data?.data as { conferenceSid: string; transferCallSid: string } | undefined
      set({
        transferStatus: 'connected',
        conferenceSid: result?.conferenceSid || null,
        transferCallSid: result?.transferCallSid || null,
        originalCallSid: callSid || null,
      })
    } catch (err) {
      logger.warn('[Softphone] initiateWarmTransfer failed', err)
      set({
        transferStatus: 'idle',
        transferTarget: null,
        error: 'Failed to initiate transfer',
      })
    }
  },

  completeWarmTransfer: async () => {
    const { conferenceSid, activeCall, activeCallSid } = get()
    if (!conferenceSid) return

    const originalCallSid =
      activeCallSid || (activeCall?.parameters as Record<string, string>)?.CallSid || ''

    try {
      await apiClient.post('/api/telephony/transfer/complete', {
        conferenceSid,
        originalCallSid,
      })
      set({ transferStatus: 'completed' })
      // Reset after brief delay
      setTimeout(() => {
        set({
          transferStatus: 'idle',
          transferTarget: null,
          conferenceSid: null,
          transferCallSid: null,
          originalCallSid: null,
        })
      }, 2000)
    } catch {
      set({ error: 'Failed to complete transfer' })
    }
  },

  cancelWarmTransfer: async () => {
    const { conferenceSid, transferCallSid, originalCallSid } = get()
    if (!conferenceSid || !transferCallSid || !originalCallSid) return

    try {
      await apiClient.post('/api/telephony/transfer/cancel', {
        conferenceSid,
        transferCallSid,
        originalCallSid,
      })
      set({ transferStatus: 'cancelled' })
      setTimeout(() => {
        set({
          transferStatus: 'idle',
          transferTarget: null,
          conferenceSid: null,
          transferCallSid: null,
          originalCallSid: null,
        })
      }, 2000)
    } catch {
      set({ error: 'Failed to cancel transfer' })
    }
  },

  resetTransfer: () =>
    set({
      transferStatus: 'idle',
      transferTarget: null,
      conferenceSid: null,
      transferCallSid: null,
      originalCallSid: null,
    }),

  clearLastEndedCall: () => {
    clearPersistedLastEndedCall()
    set({ lastEndedCall: null })
  },

  forceReconnect: async () => {
    // Reset the breaker so attemptTokenRefresh stops short-circuiting at
    // attempt 0. Cancel any pending backoff timer so the manual retry runs
    // immediately instead of waiting on the next scheduled run.
    cancelTokenRefresh()
    set({ tokenRefreshExhausted: false, phoneOffline: true })
    const device = get().device
    if (device) {
      attemptTokenRefresh(device, 0)
      return
    }
    // Device was torn down — re-init from scratch.
    await get().initDevice()
  },
}))

// ─────────────────────────────────────────────────────────────────────────────
// Theme 6 — Cross-tab leader election wiring
// ─────────────────────────────────────────────────────────────────────────────
// On leadership loss (another tab became leader, or this tab joined a session
// where another tab already held the device) we tear down the local Device so
// only the leader has an active twilio.Device. On leadership gain after
// initial mount we re-init.

let leaderWired = false
function ensureLeaderWired(): void {
  if (leaderWired) return
  leaderWired = true
  startLeaderElection()
  onLeaderChange((leader) => {
    if (!leader) {
      const state = useCallStore.getState()
      if (state.device) {
        state.destroyDevice()
      }
    } else {
      // Becoming leader after start — if there's no device yet, the
      // mounted softphone effect already called initDevice and it bailed
      // because we weren't leader yet. Re-attempt now.
      const state = useCallStore.getState()
      if (!state.device) {
        void state.initDevice()
      }
    }
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Theme 6 — Post-call card persistence (sessionStorage)
// ─────────────────────────────────────────────────────────────────────────────
// The post-call card lives in callStore.lastEndedCall. Without persistence,
// reloading the tab while the card is visible drops it on the floor with no
// way to recover the call data. We save to sessionStorage (per-tab, cleared
// on tab close) so it rehydrates on reload but doesn't bleed into a new
// session in a fresh tab.

const LAST_ENDED_CALL_KEY = 'florite.callStore.lastEndedCall.v1'
// Don't rehydrate cards older than this — stale cards from yesterday's
// session shouldn't appear when the user comes back tomorrow.
const LAST_ENDED_CALL_MAX_AGE_MS = 60 * 60 * 1000 // 1 hour

function loadPersistedLastEndedCall(): LastEndedCall | null {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(LAST_ENDED_CALL_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as LastEndedCall
    if (
      !parsed ||
      typeof parsed.endedAt !== 'number' ||
      Date.now() - parsed.endedAt > LAST_ENDED_CALL_MAX_AGE_MS
    ) {
      sessionStorage.removeItem(LAST_ENDED_CALL_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function persistLastEndedCall(call: LastEndedCall): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.setItem(LAST_ENDED_CALL_KEY, JSON.stringify(call))
  } catch {
    /* quota or disabled storage — best-effort */
  }
}

function clearPersistedLastEndedCall(): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.removeItem(LAST_ENDED_CALL_KEY)
  } catch {
    /* best-effort */
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// AC-21 Favicon flash fallback
// ─────────────────────────────────────────────────────────────────────────────

let faviconFlashInterval: ReturnType<typeof setInterval> | null = null
let faviconOriginalHref: string | null = null
let faviconShowingAlt = false

const ALERT_FAVICON_DATA_URL =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><circle cx="16" cy="16" r="14" fill="%23ef4444"/></svg>',
  )

// ─── AC-4 token-refresh backoff ─────────────────────────────────────────────
// Schedule a token refresh with exponential backoff. Delays: 1s, 2s, 4s, 8s,
// 30s (cap). Keeps retrying until success; first success flips phoneOffline
// back to false. Each `cancelTokenRefresh()` both clears any pending timer
// AND sets a cancel token so an in-flight fetch doesn't update a destroyed
// device when it finally resolves.
let tokenRefreshTimer: ReturnType<typeof setTimeout> | null = null
let tokenRefreshToken = 0 // bumps on every cancel; in-flight runs hold the original
let initInFlight = false // StrictMode double-invoke guard for initDevice
let initDeviceToken = 0 // bumped on every destroyDevice; in-flight inits hold their own copy

export function cancelTokenRefresh(): void {
  if (tokenRefreshTimer) {
    clearTimeout(tokenRefreshTimer)
    tokenRefreshTimer = null
  }
  tokenRefreshToken++
}

// FOR-TRANSPORT — module-level network-change recovery
// Wired once on first initDevice; survives destroy/init cycles so callers don't
// have to re-register. Each handler is a no-op when there's no device or the
// device isn't currently flagged offline.
let networkListenersWired = false
export function ensureNetworkListenersWired(): void {
  if (networkListenersWired) return
  if (typeof window === 'undefined') return
  networkListenersWired = true

  const tryReconnect = (reason: string) => {
    const state = useCallStore.getState()
    const device = state.device
    if (!device) return
    // Only nudge when something has actually gone wrong — if the WS is healthy
    // we'd just thrash the auth endpoint on every tab focus.
    if (!state.phoneOffline) return
    logger.info(`[Softphone] network signal '${reason}' — retrying register`)
    // Reset breaker so user doesn't have to wait for the long backoff after
    // a real connectivity change.
    cancelTokenRefresh()
    useCallStore.setState({ tokenRefreshExhausted: false })
    attemptTokenRefresh(device, 0)
  }

  window.addEventListener('online', () => tryReconnect('online'))
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') tryReconnect('visible')
    })
  }
}

/** @internal — test-only reset hook. */
export function __resetNetworkListenersForTests(): void {
  networkListenersWired = false
}

/**
 * Feature-detect HTMLAudioElement.setSinkId. Present on Android Chrome,
 * desktop Chrome/Edge, and modern Firefox. Absent on iOS Safari (all
 * versions through current). When false, the speaker toggle UI is
 * hidden — there's no way to swap audio routing from a web app on iOS.
 */
export function audioRouteSupported(): boolean {
  if (typeof HTMLAudioElement === 'undefined') return false
  // setSinkId is on the prototype — not on instances by default.
  return typeof (HTMLAudioElement.prototype as unknown as { setSinkId?: unknown }).setSinkId === 'function'
}

type AudioRoute = 'speaker' | 'earpiece'
type SinkAudioElement = HTMLAudioElement & { setSinkId: (id: string) => Promise<void> }

/**
 * Apply a logical audio route to an audio element via setSinkId.
 *
 *   • 'earpiece' → setSinkId('communications')
 *       Chrome's special "communications" sink is the OS comms-audio
 *       category. On Android during a WebRTC call this routes through
 *       the earpiece (with appropriate echo cancellation). On desktop
 *       it usually maps to the configured comms device or default
 *       output. Falls back to 'default' if the browser rejects
 *       'communications' (some Firefox builds, some embedded browsers).
 *
 *   • 'speaker' → setSinkId('default'); on Android Chrome this typically
 *       routes media-category audio through the loudspeaker. If 'default'
 *       happens to map back to the earpiece on this device (rare, varies
 *       by Android version), falls back to enumerating an audiooutput
 *       device whose label hints at "speaker"/"loud" and pinning to it.
 *
 * Returns true if at least one setSinkId call succeeded.
 *
 * No-op (returns false) when the browser has no setSinkId support.
 */
export async function applyAudioRoute(
  el: HTMLAudioElement,
  route: AudioRoute,
): Promise<boolean> {
  if (!audioRouteSupported()) return false
  const sinkEl = el as SinkAudioElement
  const tryIds = route === 'earpiece' ? ['communications', 'default'] : ['default']
  for (const id of tryIds) {
    try {
      await sinkEl.setSinkId(id)
      return true
    } catch (err) {
      logger.warn(`[Softphone] setSinkId("${id}") failed`, err)
    }
  }
  // Speaker fallback: hunt for an enumerated loudspeaker device by label.
  if (route === 'speaker') {
    const id = await pickLoudspeakerDeviceId()
    if (id) {
      try {
        await sinkEl.setSinkId(id)
        return true
      } catch (err) {
        logger.warn('[Softphone] setSinkId(loudspeakerId) fallback failed', err)
      }
    }
  }
  return false
}

/**
 * Probe `navigator.mediaDevices.enumerateDevices()` for an audiooutput
 * device that is NOT the OS default — i.e. the loudspeaker on a phone, or
 * an external speaker/HDMI on desktop. Returns null when:
 *   • the API is missing (iOS Safari),
 *   • only a single "default" output is enumerated,
 *   • permission hasn't been granted yet so labels are empty.
 * Used as a last-resort fallback inside applyAudioRoute when the symbolic
 * sink IDs ('default', 'communications') don't yield the desired routing.
 */
export async function pickLoudspeakerDeviceId(): Promise<string | null> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
    return null
  }
  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    const outputs = devices.filter((d) => d.kind === 'audiooutput')
    if (outputs.length < 2) return null
    // Prefer something whose label hints at speakerphone/loudspeaker,
    // otherwise just pick the first non-default entry. Android Chrome
    // typically exposes "Speakerphone" and "Default"; picking anything
    // non-default is usually the right call.
    const speakerish = outputs.find((d) =>
      /speaker|loud/i.test(d.label) && d.deviceId !== 'default',
    )
    const nonDefault = outputs.find((d) => d.deviceId && d.deviceId !== 'default')
    return speakerish?.deviceId ?? nonDefault?.deviceId ?? null
  } catch {
    return null
  }
}

/**
 * Resume Twilio's shared AudioContext if it's been suspended. Called on
 * every user gesture by Softphone.tsx — a belt-and-suspenders guard against
 * Safari/Chrome silently suspending the context mid-session (which
 * manifests as "sound goes out but I can't hear" on reconnects).
 */
/**
 * iOS Safari one-way-audio fix.
 *
 * On iOS, `HTMLAudioElement.prototype.setSinkId` is undefined, so the
 * Twilio Voice SDK's PeerConnection falls into `_fallbackOnAddTrack`,
 * which creates a detached <audio> element via document.createElement
 * and calls .play() from inside the WebRTC ontrack callback. That play()
 * is NOT in a user-gesture stack, so iOS' autoplay policy rejects it
 * silently — the user is heard on the remote end (mic path is fine) but
 * hears nothing back.
 *
 * Our fix: maintain our own <audio autoPlay playsInline> element rendered
 * by React (registered via setRemoteAudioElement), attach the call's
 * remote MediaStream to it on 'accept', and play. We prime the element
 * earlier from the synchronous click handler (primeRemoteAudio) so iOS
 * has already user-activated it by the time the WebRTC stream lands.
 */
export function attachRemoteAudioToCall(
  call: Call,
  audioEl: HTMLAudioElement | null,
): void {
  if (!audioEl) {
    logger.warn('[Softphone] attachRemoteAudio: no audio element registered')
    return
  }
  type CallWithStreams = Call & { getRemoteStream?: () => MediaStream | undefined }
  const remoteStream = (call as CallWithStreams).getRemoteStream?.()
  const attach = (stream: MediaStream) => {
    try {
      if (audioEl.srcObject !== stream) {
        audioEl.srcObject = stream
      }
      // Re-apply current speaker/earpiece preference on every attach so
      // the user's last choice survives subsequent calls (Android Chrome
      // resets sinkId back to default when srcObject is reassigned).
      // Skipped on platforms with no setSinkId (iOS) — returns false fast.
      const speakerOn = useCallStore.getState().isSpeakerOn
      if (speakerOn !== null) {
        void applyAudioRoute(audioEl, speakerOn ? 'speaker' : 'earpiece')
      }
      const p = audioEl.play()
      if (p && typeof p.then === 'function') {
        p.then(
          () => logger.info('[Softphone] remote audio playing'),
          (err: unknown) => logger.warn('[Softphone] remote audio play() rejected', err),
        )
      }
    } catch (err) {
      logger.warn('[Softphone] failed to attach remote audio', err)
    }
  }
  if (remoteStream && remoteStream.getAudioTracks().length > 0) {
    attach(remoteStream)
    return
  }
  // Twilio also emits an 'audio' event with the HTMLAudioElement it created
  // internally — its srcObject is the remote stream. Listening for it covers
  // the SDK-internal path on browsers where _isSinkSupported flips after we
  // first checked, and gives a backup for the rare case getRemoteStream
  // returns undefined transiently before the track arrives.
  type AudioEvent = HTMLAudioElement & { srcObject?: MediaStream }
  const onSdkAudio = (sdkAudio: AudioEvent) => {
    const stream = sdkAudio.srcObject
    if (stream) attach(stream)
  }
  type CallWithOn = Call & { on?: (ev: string, fn: (a: AudioEvent) => void) => void }
  ;(call as CallWithOn).on?.('audio', onSdkAudio)
}

export async function resumeTwilioAudio(): Promise<void> {
  type DeviceStatic = { audioContext?: AudioContext }
  const ctx = (Device as unknown as DeviceStatic).audioContext
  if (ctx && ctx.state === 'suspended') {
    try {
      await ctx.resume()
    } catch {
      /* best-effort — nothing to do if the browser refuses */
    }
  }
}

function backoffDelayMs(attempt: number): number {
  // 0→1s, 1→2s, 2→4s, 3→8s, 4+→30s cap (matches AC-4 doc string)
  if (attempt <= 0) return 1_000
  if (attempt === 1) return 2_000
  if (attempt === 2) return 4_000
  if (attempt === 3) return 8_000
  return 30_000
}

const MAX_TOKEN_REFRESH_ATTEMPTS = 10

export function attemptTokenRefresh(device: Device, attempt: number): void {
  // Circuit breaker: stop retrying after MAX_TOKEN_REFRESH_ATTEMPTS failures.
  if (attempt >= MAX_TOKEN_REFRESH_ATTEMPTS) {
    logger.warn('[Softphone] token refresh exhausted after max attempts — giving up')
    useCallStore.setState({ phoneOffline: true, tokenRefreshExhausted: true })
    return
  }

  // Cancel any pending timer but keep the current cancel-token — we're the
  // caller, we want the scheduled run to fire.
  if (tokenRefreshTimer) {
    clearTimeout(tokenRefreshTimer)
    tokenRefreshTimer = null
  }
  const myToken = tokenRefreshToken
  const run = async () => {
    tokenRefreshTimer = null
    // Bail before touching network if a teardown happened while we were idle.
    if (myToken !== tokenRefreshToken) return
    try {
      const { data: refreshData } = await apiClient.get('/api/telephony/token')
      // Re-check AFTER the await — teardown may have happened mid-flight.
      if (myToken !== tokenRefreshToken) return
      const newToken = refreshData?.data?.token
      if (!newToken) throw new Error('no token in response')
      device.updateToken(newToken)
      // If transport was lost (31009) or the Device emitted 'unregistered',
      // updateToken alone refreshes credentials but does NOT re-open the
      // signaling WS. We must explicitly re-register. Twilio SDK v2 exposes
      // the current registration state on Device.state — anything other than
      // 'registered' means we need to re-register before calls will route.
      const deviceState = (device as unknown as { state?: string }).state
      if (deviceState && deviceState !== 'registered') {
        await device.register()
      }
      useCallStore.setState({ phoneOffline: false, tokenRefreshExhausted: false })
    } catch (err) {
      if (myToken !== tokenRefreshToken) return
      logger.warn(
        `[Softphone] token refresh failed (attempt ${attempt + 1}) — retrying in ${backoffDelayMs(attempt + 1) / 1000}s`,
        err,
      )
      useCallStore.setState({ phoneOffline: true })
      attemptTokenRefresh(device, attempt + 1)
    }
  }
  if (attempt === 0) {
    void run()
  } else {
    tokenRefreshTimer = setTimeout(() => { void run() }, backoffDelayMs(attempt))
  }
}

function startFaviconFlash(): void {
  if (typeof document === 'undefined') return
  if (faviconFlashInterval) return

  const link =
    (document.querySelector("link[rel='icon']") as HTMLLinkElement | null) ??
    (document.querySelector("link[rel='shortcut icon']") as HTMLLinkElement | null)
  if (!link) return

  faviconOriginalHref = link.href
  faviconFlashInterval = setInterval(() => {
    faviconShowingAlt = !faviconShowingAlt
    link.href = faviconShowingAlt ? ALERT_FAVICON_DATA_URL : (faviconOriginalHref ?? link.href)
  }, 600)
}

function stopFaviconFlash(): void {
  if (typeof document === 'undefined') return
  if (faviconFlashInterval) {
    clearInterval(faviconFlashInterval)
    faviconFlashInterval = null
  }
  if (faviconOriginalHref) {
    const link =
      (document.querySelector("link[rel='icon']") as HTMLLinkElement | null) ??
      (document.querySelector("link[rel='shortcut icon']") as HTMLLinkElement | null)
    if (link) link.href = faviconOriginalHref
    faviconOriginalHref = null
    faviconShowingAlt = false
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// AC-34 Connection quality sampling — Twilio Call 'sample' event
// ─────────────────────────────────────────────────────────────────────────────

type SampleShape = {
  totals?: { packetsLost?: number; packetsReceived?: number }
  jitter?: number
  mos?: number
  rtt?: number
}

let qualitySampleHandler: ((sample: SampleShape) => void) | null = null
let qualitySampledCall: Call | null = null

function deriveQuality(sample: SampleShape): ConnectionQuality {
  const received = sample.totals?.packetsReceived ?? 0
  const lost = sample.totals?.packetsLost ?? 0
  const lossPct = received + lost > 0 ? (lost / (received + lost)) * 100 : 0
  const jitterMs = (sample.jitter ?? 0) * 1000 // Twilio reports seconds
  const rttMs = (sample.rtt ?? 0) * 1000

  if (lossPct < 1 && jitterMs < 30 && rttMs < 150) return 'good'
  if (lossPct < 3 && jitterMs < 60 && rttMs < 300) return 'fair'
  return 'poor'
}

function startQualitySampling(call: Call): void {
  stopQualitySampling()
  qualitySampledCall = call
  qualitySampleHandler = (sample: SampleShape) => {
    const quality = deriveQuality(sample)
    useCallStore.setState({ connectionQuality: quality })
  }
  try {
    // Twilio Voice SDK emits 'sample' periodically during a call; cast to a
    // loose EventEmitter-style shape because the generic Call type doesn't
    // expose 'sample' in its TypeScript definitions.
    const emitter = call as unknown as {
      on: (evt: string, fn: (s: SampleShape) => void) => void
    }
    emitter.on('sample', qualitySampleHandler)
  } catch {
    /* older SDKs may not emit 'sample' — quality stays null. */
  }
}

function stopQualitySampling(): void {
  const call = qualitySampledCall
  if (call && qualitySampleHandler) {
    try {
      const emitter = call as unknown as {
        off: (evt: string, fn: (s: SampleShape) => void) => void
      }
      emitter.off('sample', qualitySampleHandler)
    } catch {
      /* no-op */
    }
  }
  qualitySampledCall = null
  qualitySampleHandler = null
}

// ─────────────────────────────────────────────────────────────────────────────
// AC-38 Last-ended call capture for post-call card
// ─────────────────────────────────────────────────────────────────────────────

function captureLastEndedCall(call: Call, direction: 'inbound' | 'outbound'): void {
  const state = useCallStore.getState()
  const params = (call.parameters as Record<string, string>) || {}
  const callSid = params.CallSid
  const duration = state.callStartTime ? Math.floor((Date.now() - state.callStartTime) / 1000) : 0
  const quality = state.connectionQuality ?? undefined

  const lastEndedCall: LastEndedCall = {
    callSid,
    contactId: state.callerInfo?.contactId || state.pendingContactId || undefined,
    contactName: state.callerInfo?.name,
    number: state.callerInfo?.number || 'Unknown',
    duration,
    direction,
    context: state.pendingContext ?? undefined,
    endedAt: Date.now(),
    finalQuality: quality,
  }

  persistLastEndedCall(lastEndedCall)
  useCallStore.setState({ lastEndedCall, pendingContext: null, pendingContactId: null })

  // Persist final quality to the backend (best-effort — never block the UI).
  if (callSid && quality) {
    void apiClient
      .patch(`/api/telephony/sessions/${callSid}/quality`, { quality })
      .catch(() => { /* best-effort */ })
  }
}
